import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient, axiosClient, useAuth } from '../../auth-lib';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OrgAdminLayout from '../components/OrgAdminLayout';
import BulkActionBar from '../components/BulkActionBar';
import ImportModal from '../components/ImportModal';
import { Button, Select, EmptyState, useToast } from '../../design-system';
import {
  TASK_STATUSES, DUE_FILTERS, ALL_DUE_VALUES, dueFilterLabel,
  TASK_SORTS, dueAging,
} from '../../lib/taskStatus';
import '../styles/OrgAdminTasks.css';
import '../styles/BulkActions.css';
import { useLiveRefresh } from '../../lib/useLiveRefresh';
import { GROUP_BYS, groupTasks } from '../../lib/taskViews';
import TaskListView from '../components/tasks/TaskListView';
import TaskBoardView from '../components/tasks/TaskBoardView';
import TaskTableView from '../components/tasks/TaskTableView';
import TaskTimelineView from '../components/tasks/TaskTimelineView';
import '../styles/TaskViews.css';

// The four ways of reading the same docket.
//
// ⚠️ ONE FETCH, ONE FILTERED SET, FOUR RENDERERS. The views differ only in how
// they arrange `filteredTasks` — none of them fetches, filters or sorts on its
// own. A board that asked the server for its own rows would drift from the list
// beside it the moment a filter changed, and "the board says 15, the list says
// 12" is unanswerable from the outside.
const VIEWS = [
  { value: 'list', label: 'List' },
  { value: 'board', label: 'Board' },
  { value: 'table', label: 'Table' },
  { value: 'timeline', label: 'Timeline' },
];

// A task's client (primary, else the first of its linked clients) and its
// compliance "type" (via instance -> enrollment -> ComplianceType). Ad-hoc
// tasks have no compliance type and are grouped under "Others".
const clientOf = (t) => t.clientId || (t.clientIds && t.clientIds[0]) || null;
const typeOf = (t) => t.complianceInstanceId?.enrollmentId?.complianceTypeId || null;

const MANAGER_LEVEL = 3;
const STATUS_VALUES = TASK_STATUSES.map((s) => s.value);
// ⚠️ Validated against ALL_DUE_VALUES, not just the dropdown's own — the
// dashboard's deadline tiles deep-link to windows the dropdown does not list,
// and checking against DUE_FILTERS alone silently dropped them back to "all".
const DUE_VALUES = ALL_DUE_VALUES;

function OrgAdminTasks() {
  const revision = useLiveRefresh();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  // Deep-linked filters, e.g. from the dashboard's Task summary
  // (/org-admin/tasks?status=todo) or an "Overdue" tile (?due=overdue).
  const initialStatus = searchParams.get('status');
  const initialDue = searchParams.get('due');
  const [filter, setFilter] = useState(
    STATUS_VALUES.includes(initialStatus) ? initialStatus : 'all'
  );
  const [dueFilter, setDueFilter] = useState(
    DUE_VALUES.includes(initialDue) ? initialDue : 'all'
  );
  const [sort, setSort] = useState('dueDate');
  // ?client=<id> — where a client's "Tasks" utility lands. Unlike status and
  // due, this one is NOT validated against a list: the set of valid clients is
  // whatever the fetched rows turn out to contain, and an id matching none of
  // them simply filters to nothing, which is the honest answer for a client
  // with no tasks.
  const [clientFilter, setClientFilter] = useState(searchParams.get('client') || 'all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  // ⚠️ REMEMBERED PER BROWSER, not per session. Which view somebody reads their
  // docket in is a standing preference — a partner who works on the board does
  // so every morning — and resetting to List on every visit makes the other
  // three feel like a detour. Wrapped: localStorage throws outright in a
  // private window with site data blocked.
  const [view, setView] = useState(() => {
    try {
      const v = window.localStorage.getItem('tasks-view');
      return VIEWS.some((x) => x.value === v) ? v : 'list';
    } catch { return 'list'; }
  });
  const [groupBy, setGroupBy] = useState('status');
  const [staff, setStaff] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();

  const isManager = (user?.role?.level || 0) >= MANAGER_LEVEL;

  // Due window and sort are applied SERVER-side (they need the whole result
  // set, not the page in hand), so changing either refetches. Client, type and
  // status stay client-side filters over the fetched rows, as before.
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const params = { sort };
      if (dueFilter !== 'all') params.due = dueFilter;
      if (filter !== 'all') params.status = filter;
      const response = await apiClient.get('/api/tasks', { params });
      setTasks(response.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, dueFilter, filter, revision]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Re-apply on arriving via a new deep link (React Router reuses the component,
  // so the initial state alone wouldn't update).
  useEffect(() => {
    const s = searchParams.get('status');
    const d = searchParams.get('due');
    const c = searchParams.get('client');
    if (s && STATUS_VALUES.includes(s)) setFilter(s);
    if (d && DUE_VALUES.includes(d)) setDueFilter(d);
    if (c) setClientFilter(c);
  }, [searchParams]);

  // Distinct clients present in the loaded tasks, for the client filter.
  const clientOptions = useMemo(() => {
    const map = new Map();
    tasks.forEach((t) => {
      const c = clientOf(t);
      if (c?._id) map.set(c._id, c.displayName || '—');
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tasks]);

  // Distinct compliance types present, plus an "Others" bucket for ad-hoc tasks.
  const typeOptions = useMemo(() => {
    const map = new Map();
    let hasOther = false;
    tasks.forEach((t) => {
      const ct = typeOf(t);
      if (ct?.code) map.set(ct.code, ct.name || ct.code);
      else hasOther = true;
    });
    const list = [...map.entries()]
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    if (hasOther) list.push({ code: 'OTHER', name: 'Others' });
    return list;
  }, [tasks]);

  const filteredTasks = useMemo(() => tasks.filter((t) => {
    if (clientFilter !== 'all' && (clientOf(t)?._id || '') !== clientFilter) return false;
    if (typeFilter !== 'all' && (typeOf(t)?.code || 'OTHER') !== typeFilter) return false;
    return true;
  }), [tasks, clientFilter, typeFilter]);

  // Selection is kept to what's currently visible: filtering away a selected
  // row and then hitting "Reschedule" would act on tasks nobody can see.
  const visibleIds = useMemo(() => filteredTasks.map((t) => t._id), [filteredTasks]);
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => visibleIds.includes(id)));
  }, [visibleIds]);

  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const toggleAll = () => setSelectedIds(allVisibleSelected ? [] : visibleIds);
  const toggleOne = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const overdueCount = useMemo(
    () => filteredTasks.filter((t) => dueAging(t).tone === 'overdue').length,
    [filteredTasks]
  );

  useEffect(() => {
    try { window.localStorage.setItem('tasks-view', view); } catch { /* private window */ }
  }, [view]);

  // The assignee dropdown's options. Only the table edits assignees, so this is
  // fetched once and shared rather than per row.
  useEffect(() => {
    if (!isManager) return;
    axiosClient.get('/users')
      .then((r) => setStaff(r.data || []))
      // A failed staff list leaves the dropdown with just the current holder
      // and "Unassigned" — worse than the full list, better than a broken page.
      .catch(() => setStaff([]));
  }, [isManager]);

  const groups = useMemo(() => groupTasks(filteredTasks, groupBy), [filteredTasks, groupBy]);

  const openTask = useCallback((t) => navigate(`/org-admin/tasks/${t._id}`), [navigate]);

  /**
   * One field, changed in place, from the table or the board.
   *
   * ⚠️ OPTIMISTIC, AND ROLLED BACK ON REFUSAL. The row has to move the instant
   * it is dropped or the drag reads as having failed — but a server that
   * refuses (a completed task is frozen, a blocked client, a lost permission)
   * must not leave the screen asserting something the register does not hold.
   * The previous value is restored and the reason said out loud.
   */
  const patchTask = useCallback(async (task, changes) => {
    const before = tasks;
    setTasks((prev) => prev.map((t) => (t._id === task._id ? { ...t, ...changes } : t)));
    try {
      const { data } = await apiClient.patch(`/api/tasks/${task._id}`, changes);
      // Replaced with the SERVER's row, not left as the optimistic guess: a
      // status change can move fields this screen never sent (completedAt, the
      // approval state), and keeping the guess would show a task the server
      // does not have.
      setTasks((prev) => prev.map((t) => (t._id === task._id ? { ...t, ...data } : t)));
    } catch (err) {
      setTasks(before);
      toast.danger(err.response?.data?.error || 'Could not save that change');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await apiClient.delete(`/api/tasks/${taskId}`);
      setTasks((prev) => prev.filter((t) => t._id !== taskId));
      toast.success('Task deleted');
    } catch (error) {
      toast.danger(error.response?.data?.error || 'Failed to delete task');
    }
  };

  return (
    <OrgAdminLayout title="Tasks Management" subtitle="Create and manage organization tasks">
      <div className="tasks-management">
        <div className="controls">
          <div className="filters">
            {/* Due window first — at quarter-end it's the filter that matters. */}
            <Select value={dueFilter} onChange={(e) => setDueFilter(e.target.value)} className="filter-select">
              {/* ⚠️ A deep link from a dashboard tile can set a window that is
                  NOT one of the dropdown's own (see DUE_DEEP_LINKS). A native
                  <select> whose value matches no option renders BLANK, so the
                  page would silently claim no due filter while showing a
                  filtered list. Render the active one as an extra option so the
                  control states what is actually applied; picking anything else
                  drops it, which is the right way out of a narrow window. */}
              {!DUE_FILTERS.some((d) => d.value === dueFilter) && (
                <option value={dueFilter}>{dueFilterLabel(dueFilter)}</option>
              )}
              {DUE_FILTERS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.value === 'all' ? 'Any due date' : d.label}
                </option>
              ))}
            </Select>

            <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
              <option value="all">All Tasks</option>
              {TASK_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>

            <Select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className="filter-select">
              <option value="all">All Clients</option>
              {/* ⚠️ The options are built from the LOADED TASKS, so a client
                  deep-linked from their own page (?client=…) who has no tasks
                  yet matches no option — and a native <select> whose value
                  matches nothing renders BLANK, i.e. looks like "All Clients"
                  while the empty list says otherwise. Carry the filter as its
                  own option so the control tells the truth. */}
              {clientFilter !== 'all' && !clientOptions.some((c) => c.id === clientFilter) && (
                <option value={clientFilter}>This client (no tasks)</option>
              )}
              {clientOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>

            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="filter-select">
              <option value="all">All Types</option>
              {typeOptions.map((ty) => (
                <option key={ty.code} value={ty.code}>{ty.name}</option>
              ))}
            </Select>

            <Select value={sort} onChange={(e) => setSort(e.target.value)} className="filter-select">
              {TASK_SORTS.map((s) => (
                <option key={s.value} value={s.value}>Sort: {s.label}</option>
              ))}
            </Select>

            {/* ⚠️ Hidden on the table, which is the one view that does not
                group — it sorts. Leaving the control there would offer a
                setting with no visible effect. */}
            {view !== 'table' && (
              <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="filter-select">
                {GROUP_BYS.map((g) => (
                  <option key={g.value} value={g.value}>Group: {g.label}</option>
                ))}
              </Select>
            )}
          </div>

          <div className="tasks-toolbar-actions">
            <div className="tv-switch" role="group" aria-label="How to show the tasks">
              {VIEWS.map((v) => (
                <button
                  key={v.value}
                  type="button"
                  aria-pressed={view === v.value}
                  onClick={() => setView(v.value)}
                >
                  {v.label}
                </button>
              ))}
            </div>
            <Button variant="secondary" onClick={() => setImportOpen(true)}>Import</Button>
            <Button variant="primary" onClick={() => navigate('/org-admin/tasks/create')}>
              + Create Task
            </Button>
          </div>
        </div>

        {!loading && (
          <p className="tasks-count">
            {/* ⚠️ MOVED OFF THE TABLE HEADER. Select-all used to be a
                checkbox in the old table's first column, so swapping the view
                in would have left three of the four with no way to select a
                whole page for BulkActionBar. It belongs beside the count,
                which every view has. */}
            <button className="action-link" onClick={toggleAll}>
              {allVisibleSelected ? 'Clear selection' : 'Select all'}
            </button>
            {' · '}
            Showing {filteredTasks.length} of {tasks.length} tasks
            {overdueCount > 0 && dueFilter !== 'overdue' && (
              <>
                {' · '}
                <button className="action-link" onClick={() => setDueFilter('overdue')}>
                  {overdueCount} overdue
                </button>
              </>
            )}
          </p>
        )}

        <BulkActionBar
          selectedIds={selectedIds}
          tasks={filteredTasks}
          onClear={() => setSelectedIds([])}
          onDone={fetchTasks}
          canApprove={isManager}
          canEdit
        />

        {!loading && filteredTasks.length === 0 ? (
          // `dueFilterLabel`, not a DUE_FILTERS lookup — a deep-linked window is
          // not in that list, and the lookup rendered "Nothing undefined".
          <EmptyState
            icon="✓"
            title={dueFilter === 'all' ? 'No tasks found' : `Nothing ${dueFilterLabel(dueFilter).toLowerCase()}`}
            action={<Button variant="secondary" onClick={() => navigate('/org-admin/tasks/create')}>Create your first task</Button>}
          />
        ) : view === 'list' ? (
          <TaskListView
            groups={groups}
            selectedIds={selectedIds}
            onToggle={toggleOne}
            onOpen={openTask}
          />
        ) : view === 'board' ? (
          <TaskBoardView
            groups={groups}
            groupBy={groupBy}
            onOpen={openTask}
            onPatch={patchTask}
            canEdit={isManager}
          />
        ) : view === 'timeline' ? (
          <TaskTimelineView groups={groups} onOpen={openTask} />
        ) : (
          <TaskTableView
            tasks={filteredTasks}
            selectedIds={selectedIds}
            onToggle={toggleOne}
            onOpen={openTask}
            onPatch={patchTask}
            onDelete={handleDelete}
            staff={staff}
            canEdit={isManager}
          />
        )}
      </div>

      <ImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={fetchTasks}
        basePath="/api/tasks"
        title="Import tasks from a spreadsheet"
        noun="task"
        nounPlural="tasks"
        templateFile="task-import-template.xlsx"
        describeTemplate="An Excel file with the columns we accept, dropdowns for priority and bill type, and sheets listing your clients and your staff's emails. One row = one task."
        describeUpload="For raising a batch in one go — an ITR task for sixty clients, say. Every task is created as Pending. Recurring work isn't set up here; use a service's due rule or the Recurring card on the task."
        rowLabel={(r) => r.title}
      />
    </OrgAdminLayout>
  );
}

export default OrgAdminTasks;
